import { useRef, useEffect, useState } from 'react';
import {
  FORMAT,
  formatDate,
  resizeImgFile,
  convertWeight,
} from '../../../../lib/utils';
import { WEIGHT_UNIT } from '../../../../types/enum';
import axios from 'axios';
import request from '../../../../lib/request';
import userCircle from '../../../../assets/svg/user-circle.svg';
import CapturePhoto from '../../../../components/capturePhoto';
import Premium from '../../../../components/premium';
import PremiumCard from './premiumCard';
import ProgramCard from './programCard';
import Menu from './menu';
import CurrentDialog from './currentDialog';
import usePreference from '../../../../hook/usePreference';

const PersonalInfo = ({ signOut, refreshUser }) => {
  const { preference, updateUserPreference } = usePreference();

  const fileRef = useRef(null);
  const dialogRef = useRef(null);

  const [userInfo, setUserInfo] = useState({});
  const [newUserInfo, setNewUserInfo] = useState({});
  const [infoChanged, setInfoChanged] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);

  useEffect(() => {
    getUserInfo();
  }, [preference]);

  const refreshUserInfo = async () => {
    await getUserInfo();
    await refreshUser();
  };

  const getUserInfo = async () => {
    try {
      const { result } = await request.get('/user');
      if (result) {
        if (preference.weightUnit === WEIGHT_UNIT.LB) {
          result.weight = Math.ceil(
            convertWeight(result.weight, WEIGHT_UNIT.KG, WEIGHT_UNIT.LB)
          );
        }
        setUserInfo(result);
        setNewUserInfo(result);
      }
    } catch (err) {
      console.log(err?.message);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    try {
      let resizedFile = await resizeImgFile(file);

      if (file.size < resizedFile.size) {
        resizedFile = file;
      }

      const { result } = await request.get(
        `/upload?filename=${resizedFile.name}&contentType=${resizedFile.type}&type=profile`
      );

      if (result.url && result.key) {
        await axios.put(result.url, resizedFile, {
          headers: {
            'Content-Type': resizedFile.type,
          },
        });

        await request.put('/user', {
          photoKey: result.key,
        });

        await refreshUserInfo();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleClickCapture = () => {
    if (fileRef.current && fileRef.current.value) {
      fileRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    try {
      if (infoChanged) {
        const body = {};

        if (newUserInfo.firstname !== userInfo.firstname) {
          body.firstname = newUserInfo.firstname;
        }
        if (newUserInfo.lastname !== userInfo.lastname) {
          body.lastname = newUserInfo.lastname;
        }
        if (newUserInfo.nickname !== userInfo.nickname) {
          body.nickname = newUserInfo.nickname;
        }
        if (newUserInfo.gender !== userInfo.gender) {
          body.gender = newUserInfo.gender;
        }
        if (newUserInfo.height !== userInfo.height) {
          if (newUserInfo.height > 100 && newUserInfo.height < 250) {
            body.height = newUserInfo.height;
          } else {
            throw new Error(
              'Invalid height format. Must be between 100 and 250.'
            );
          }
        }
        if (preference.weightUnit === WEIGHT_UNIT.LB) {
          if (newUserInfo.weight > 59 && newUserInfo.weight < 440) {
            body.weight = convertWeight(
              newUserInfo.weight,
              WEIGHT_UNIT.LB,
              WEIGHT_UNIT.KG
            );
          } else {
            throw new Error(
              'Invalid weight format. Must be between 59 and 440.'
            );
          }
        }
        if (preference.weightUnit === WEIGHT_UNIT.KG) {
          if (newUserInfo.weight > 29 && newUserInfo.weight < 200) {
            body.weight = newUserInfo.weight;
          } else {
            throw new Error(
              'Invalid weight format. Must be between 29 and 200.'
            );
          }
        }
        if (newUserInfo.country !== userInfo.country) {
          body.country = newUserInfo.country;
        }
        if (newUserInfo.birth !== userInfo.birth) {
          body.birth = newUserInfo.birth;
        }

        if (Object.keys(body).length > 0) {
          await request.put('/user', body);
          await refreshUserInfo();
          setInfoChanged(false);
        }
      }
    } catch (err) {
      alert(err);
    }
  };

  const handleNewUserInfo = (key) => (e) => {
    let value = String(e.target.value);
    if (key === 'height' || key === 'weight') {
      value = Number(value);
    }
    setNewUserInfo((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (userInfo[key] === value) {
      setInfoChanged(false);
    } else {
      setInfoChanged(true);
    }
  };

  const openCurrentDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  };

  const closeCurrentDialog = () => {
    if (dialogRef.current) {
      setCurrentMenu(null);
      dialogRef.current.close();
    }
  };

  const handleMenuChange = (menu) => () => {
    setCurrentMenu(menu);
    openCurrentDialog();
  };

  return (
    <>
      <div className='space-y-4'>
        <div className='flex flex-col items-center gap-2 text-white'>
          <img
            src={newUserInfo?.profileUrl ? newUserInfo?.profileUrl : userCircle}
            alt='profile'
            className={`bg-tertiary rounded-full size-24 object-cover  ${
              userInfo.activated
                ? 'border-4 border-yellow-500'
                : userInfo.programRegistered
                ? 'border-4 border-slate-500'
                : 'border border-tertiary'
            }`}
          />
          <CapturePhoto
            fileRef={fileRef}
            handleCapture={handleFileChange}
            handleClickCapture={handleClickCapture}
            border={false}
            label='Change profile'
          />
        </div>
        <div className='text-white'>
          <div className='text-xs flex items-center'>
            <div className='font-bold w-16'>Email(ID)</div>
            <div>{newUserInfo.email}</div>
          </div>
          <div className='text-xs flex items-center'>
            <div className='font-bold w-16'>Joined at</div>
            <div>
              {formatDate(new Date(newUserInfo.createdAt), FORMAT.DATE)}
            </div>
          </div>
          {(userInfo.activated || userInfo.paused) && (
            <PremiumCard
              userInfo={userInfo}
              refreshUser={refreshUser}
            />
          )}
          {userInfo.programRegistered && <ProgramCard userInfo={userInfo} />}
        </div>

        {!userInfo.activated && !userInfo.paused && (
          <Premium refreshUser={refreshUserInfo} />
        )}

        <Menu selectMenu={handleMenuChange} />

        <div className='flex justify-center pt-8'>
          <button
            className='text-xs font-bold text-red-600 text-center'
            onClick={signOut}
          >
            Sign out
          </button>
        </div>
      </div>
      <CurrentDialog
        dialogRef={dialogRef}
        closeDialog={closeCurrentDialog}
        currentMenu={currentMenu}
        newUserInfo={newUserInfo}
        infoChanged={infoChanged}
        handleNewUserInfo={handleNewUserInfo}
        handleSubmit={handleSubmit}
        preference={preference}
        updateUserPreference={updateUserPreference}
      />
    </>
  );
};

export default PersonalInfo;
